import {Box} from "@mui/material";
import {PaymentElement, useElements, useStripe} from "@stripe/react-stripe-js";
import {useNavigate} from "@tanstack/react-router";
import {useEffect} from "react";
import {useCartContext} from "@/components/Providers/CartProvider.tsx";
import {useFeeProvider} from "@/components/Providers/FeesProvider.tsx";
import {useUser} from "@/components/Providers/JWTProvider.tsx";
import useStripeCompleteSetupIntent from "@/hooks/useStripeCompleteSetupIntent.tsx";
import type {CreateSetupIntentResponse} from "@/types/CreateSetupIntentResponse.ts";

type Props = {
    cartId: string | null;
    courseSectionId: string | null;
    submitValues: any;
    payNowAmount: number;
    setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>;
    handleModalClose?: (showCancelModal?: boolean, showConfirmModal?: boolean) => void;
    stripeErrorMessage: string;
    setStripeErrorMessage: (stripeErrorMessage: string) => void;
    cartHasFees?: boolean;
};

const StripeForm = ({
    cartId,
    courseSectionId,
    submitValues,
    payNowAmount,
    setIsProcessing,
    handleModalClose,
    stripeErrorMessage,
    setStripeErrorMessage,
    cartHasFees,
}: Props) => {
    const {cart} = useCartContext();
    const stripe = useStripe();
    const elements = useElements();
    const {isEmployee, user} = useUser();
    const completeSetupIntent = useStripeCompleteSetupIntent();
    const {refreshCart, cartPaymentSuccess} = useCartContext();
    const {refreshPurchaseHistory, refreshFees} = useFeeProvider();
    const navigate = useNavigate();

    if (!(cartId || courseSectionId)) {
        setStripeErrorMessage("Cart ID or Course Section ID required for payment processing.");
    }

    const handleStripeSubmit = async () => {
        if (!(stripe && elements)) {
            return;
        }

        setIsProcessing(true);

        const result = await stripe.confirmSetup({
            elements,
            redirect: "if_required",
            confirmParams: {
                return_url:
                    `${window.origin}/payment-complete?` +
                    `cartId=${cartId}` +
                    `&payNowAmount=${submitValues?.partialPaymentAmount ?? payNowAmount}`,
            },
        });

        if (result.error !== undefined && result.error.message !== undefined) {
            setStripeErrorMessage(result.error.message);
        }

        // if redirect is not required by payment method:
        const setupIntentClientSecret = result.setupIntent?.client_secret;
        const setupIntentId = result.setupIntent?.id;

        if (user && setupIntentClientSecret && setupIntentId) {
            const retrieveSetupIntentResponse =
                await stripe.retrieveSetupIntent(setupIntentClientSecret);

            if (retrieveSetupIntentResponse.error) {
                setStripeErrorMessage(
                    retrieveSetupIntentResponse.error.message ??
                        "An unknown stripe error has occurred.",
                );
                return;
            }

            const response = await completeSetupIntent(
                user,
                setupIntentId,
                retrieveSetupIntentResponse.setupIntent.payment_method as string,
                cartId,
                courseSectionId,
                payNowAmount,
                null,
            );

            if (response.ok) {
                const json = (await response.json()) as CreateSetupIntentResponse;

                if (json.errorCode !== 0) {
                    setStripeErrorMessage(json.message);
                    return;
                }

                if (json.data.redirectUrl) {
                    window.location.href = json.data.redirectUrl;
                    return;
                }

                const cartId = cart?.id;

                await refreshCart();
                await refreshPurchaseHistory();
                await refreshFees();

                if (handleModalClose) {
                    handleModalClose(false, false);
                }

                if (user && "userType" in user && user.userType === "guest_user") {
                    await navigate({
                        to: `/cart-receipt/${cartId}`,
                    });
                } else {
                    navigate({
                        to: isEmployee
                            ? "/instructor/fees"
                            : cartHasFees
                              ? "/fees"
                              : "/my-registrations",
                        search: {purchased: "purchased"},
                    });
                }
                await cartPaymentSuccess(`Payment of $${payNowAmount.toString()} completed.`);
            } else {
                setStripeErrorMessage("An unknown error has occurred.");
            }
        }

        setIsProcessing(false);
    };

    useEffect(() => {
        if (submitValues) {
            void handleStripeSubmit();
        }
    }, [submitValues]);

    return (
        <>
            <PaymentElement options={{terms: {card: "never"}}} />
            {stripeErrorMessage && <Box sx={{color: "red"}}>{stripeErrorMessage}</Box>}
        </>
    );
};

export default StripeForm;
